import { apiEndpoints } from './common'
import * as mock from './procurementMock'

import { getAccessToken, makeRequestUrl } from '../app/helpers'
import { get, post, put, patch, remove } from '../request'

// Fully-offline mode: REACT_APP_PROCUREMENT_MOCK=true routes every procurement
// call to in-browser fake data (no backend). See procurementMock.js.
const USE_MOCK = process.env.REACT_APP_PROCUREMENT_MOCK === 'true'

// ---- Centre dashboard ----
const fetchCentreDashboard = async (queries = {}) => {
  if (USE_MOCK) return mock.fetchCentreDashboard(queries)
  const url = makeRequestUrl(apiEndpoints.procurementDashboard, queries)
  const response = await get(url)
  return response.data
}

// ---- Budgets ----
const fetchBudgets = async (queries = {}) => {
  if (USE_MOCK) return mock.fetchBudgets(queries)
  const url = makeRequestUrl(apiEndpoints.procurementBudget, {
    limit: 500,
    ...queries
  })
  const response = await get(url)
  return response.data
}

const createBudget = async (data) => {
  if (USE_MOCK) return mock.createBudget(data)
  const response = await post(apiEndpoints.procurementBudget, data)
  return response.data
}

const updateBudget = async (uuid, data) => {
  if (USE_MOCK) return mock.updateBudget(uuid, data)
  const response = await patch(
    `${apiEndpoints.procurementBudget}/${uuid}/`,
    data
  )
  return response.data
}

const deleteBudget = async (uuid) => {
  if (USE_MOCK) return mock.deleteBudget(uuid)
  const response = await remove(`${apiEndpoints.procurementBudget}/${uuid}/`)
  return response.data
}

// Move budget between two categories of the same centre/month (total-preserving).
const transferBudget = async (data) => {
  if (USE_MOCK) return mock.transferBudget(data)
  const response = await post(apiEndpoints.procurementBudgetTransfer, data)
  return response.data
}

// ---- Centres ----
// Admin budget matrix: all centres (reuses the existing commons/centre API).
const fetchCentres = async (queries = {}) => {
  if (USE_MOCK) return mock.fetchCentres(queries)
  const url = makeRequestUrl(apiEndpoints.centre, { limit: 200, ...queries })
  const response = await get(url)
  return response.data
}

// Centre picker for the centre-facing flows. Reads the APP-WIDE centre list
// (commons/centre, same source as Attendance et al.) so procurement shows all
// real centres — not just the user's linked ones. (The scoped my-centres
// endpoint still exists for when a procurement role is introduced; re-point here
// then. See AVENGERS_ITEMS #3.)
const fetchMyCentres = async () => {
  if (USE_MOCK) return mock.fetchMyCentres()
  const url = makeRequestUrl(apiEndpoints.centre, { limit: 1000 })
  const response = await get(url)
  return response.data
}

// ---- Petty Cash ----
const fetchPettyCash = async (queries = {}) => {
  if (USE_MOCK) return mock.fetchPettyCash(queries)
  const url = makeRequestUrl(apiEndpoints.procurementPettyCash, {
    limit: 500,
    ...queries
  })
  const response = await get(url)
  return response.data
}

const createPettyCash = async (data) => {
  if (USE_MOCK) return mock.createPettyCash(data)
  const response = await post(apiEndpoints.procurementPettyCash, data)
  return response.data
}

const createPettyCashBatch = async (data) => {
  if (USE_MOCK) return mock.createPettyCashBatch(data)
  const response = await post(apiEndpoints.procurementPettyCashBatch, data)
  return response.data
}

const deletePettyCash = async (uuid) => {
  if (USE_MOCK) return mock.deletePettyCash(uuid)
  const response = await remove(
    `${apiEndpoints.procurementPettyCash}/${uuid}/`
  )
  return response.data
}

const updatePettyCash = async (uuid, data) => {
  if (USE_MOCK) return mock.updatePettyCash(uuid, data)
  const response = await patch(
    `${apiEndpoints.procurementPettyCash}/${uuid}/`,
    data
  )
  return response.data
}

const fetchPettyCashSummary = async (queries = {}) => {
  if (USE_MOCK) return mock.fetchPettyCashSummary(queries)
  const url = makeRequestUrl(apiEndpoints.procurementPettyCashSummary, queries)
  const response = await get(url)
  return response.data
}

// ---- Azam's R&M register (HQ) ----
const fetchRmRegister = async (month) => {
  const url = makeRequestUrl(apiEndpoints.procurementRmRegister, { month })
  const response = await get(url)
  return response.data
}

// R&M reco per centre: paid by Finance / Azam / centre vs Alfred vs budget.
const fetchRmReco = async (month) => {
  const url = makeRequestUrl(apiEndpoints.procurementRmReco, { month })
  const response = await get(url)
  return response.data
}

const createRmRegisterLine = async (data) => {
  const response = await post(apiEndpoints.procurementRmRegister, data)
  return response.data
}

const updateRmRegisterLine = async (uuid, data) => {
  const response = await patch(
    `${apiEndpoints.procurementRmRegister}/${uuid}/`,
    data
  )
  return response.data
}

const deleteRmRegisterLine = async (uuid) => {
  const response = await remove(`${apiEndpoints.procurementRmRegister}/${uuid}/`)
  return response.data
}

// ---- Consumables ----
const fetchConsumableRoster = async (queries = {}) => {
  if (USE_MOCK) return mock.fetchConsumableRoster(queries)
  const url = makeRequestUrl(apiEndpoints.procurementConsumablesRoster, queries)
  const response = await get(url)
  return response.data
}

const saveConsumableRoster = async (data) => {
  if (USE_MOCK) return mock.saveConsumableRoster(data)
  const response = await post(apiEndpoints.procurementConsumablesRoster, data)
  return response.data
}

const fetchConsumablesHistory = async (queries = {}) => {
  if (USE_MOCK) return mock.fetchConsumablesHistory(queries)
  const url = makeRequestUrl(apiEndpoints.procurementConsumablesHistory, queries)
  const response = await get(url)
  return response.data
}

// HQ reopens a submitted consumables month so the centre can edit/resubmit.
const reopenConsumables = async (centre, month) => {
  if (USE_MOCK) return mock.reopenConsumables?.(centre, month)
  const response = await post(apiEndpoints.procurementConsumablesAdminAction, {
    centre,
    month,
    action: 'reopen'
  })
  return response.data
}

// HQ: list non-draft consumables months (submitted awaiting approval, etc.).
const fetchConsumablesPending = async (queries = {}) => {
  if (USE_MOCK) return mock.fetchConsumablesPending?.(queries) || { results: [] }
  const url = makeRequestUrl(apiEndpoints.procurementConsumablesPending, queries)
  const response = await get(url)
  return response.data
}

// HQ approves / rejects a submitted consumables month, or marks an approved
// month 'sent' (decision='sent') so the centre can confirm receipt.
const decideConsumables = async (centre, month, decision, remarks = '') => {
  if (USE_MOCK) {
    return mock.decideConsumables?.(centre, month, decision, remarks)
  }
  const response = await post(apiEndpoints.procurementConsumablesDecision, {
    centre,
    month,
    decision,
    remarks
  })
  return response.data
}

// Centre confirms receipt of a 'sent' consumables month, line by line.
// lines: [{ id, receipt_status, receipt_note }].
const confirmConsumablesReceipt = async (centre, month, lines = []) => {
  if (USE_MOCK) {
    return mock.confirmConsumablesReceipt?.(centre, month, lines)
  }
  const response = await post(apiEndpoints.procurementConsumablesReceipt, {
    centre,
    month,
    lines
  })
  return response.data
}

// ---- Program Material ----
const fetchProgramMaterial = async (queries = {}) => {
  if (USE_MOCK) return mock.fetchProgramMaterial(queries)
  const url = makeRequestUrl(apiEndpoints.procurementProgramMaterial, queries)
  const response = await get(url)
  return response.data
}

const saveProgramMaterial = async (data) => {
  if (USE_MOCK) return mock.saveProgramMaterial(data)
  const response = await post(apiEndpoints.procurementProgramMaterial, data)
  return response.data
}

const searchCatalogue = async (query = '') => {
  if (USE_MOCK) return mock.searchCatalogue(query)
  const url = makeRequestUrl(apiEndpoints.procurementCatalogue, {
    search: query,
    limit: 15
  })
  const response = await get(url)
  return response.data
}

// HQ catalogue browse tab: paginated list (search/limit/offset) -> {results, count}.
const fetchCatalogueBrowse = async (queries = {}) => {
  if (USE_MOCK) return mock.fetchCatalogueBrowse(queries)
  const url = makeRequestUrl(apiEndpoints.procurementCatalogueBrowse, {
    limit: 50,
    offset: 0,
    ...queries
  })
  const response = await get(url)
  return response.data
}

// HQ adds or edits a main-catalogue item; send uuid to edit an existing row.
const saveCatalogueItem = async (data) => {
  if (USE_MOCK) throw new Error('Catalogue editing needs the live API.')
  const response = await post(apiEndpoints.procurementCatalogueEdit, data)
  return response.data
}

const fetchProgramMaterialHistory = async (queries = {}) => {
  if (USE_MOCK) return mock.fetchProgramMaterialHistory(queries)
  const url = makeRequestUrl(
    apiEndpoints.procurementProgramMaterialHistory,
    queries
  )
  const response = await get(url)
  return response.data
}

// ---- Workflow: approvals / attachments / notifications ----
const fetchAllOrders = async (queries = {}) => {
  if (USE_MOCK) return mock.fetchAllOrders(queries)
  const url = makeRequestUrl(apiEndpoints.procurementOrders, queries)
  const response = await get(url)
  return response.data
}

// HQ 12-centre monthly board: PM + Consumables status per centre for a month.
const fetchHqMonthlyBoard = async (month) => {
  if (USE_MOCK) return { results: [] }
  const url = makeRequestUrl(apiEndpoints.procurementHqMonthlyBoard, { month })
  const response = await get(url)
  return response.data
}

// The full line list behind one board cell (for the view popup + export).
const fetchHqMonthlyBoardDetail = async (month, centre, category) => {
  if (USE_MOCK) return { title: '', columns: [], rows: [], total: '0' }
  const url = makeRequestUrl(apiEndpoints.procurementHqMonthlyBoardDetail, {
    month,
    centre,
    category
  })
  const response = await get(url)
  return response.data
}

// The dated status lifecycle of a PM/consumables order (for the tracking popup).
const fetchHqOrderTimeline = async (month, centre, category) => {
  if (USE_MOCK) return { exists: false, stages: [] }
  const url = makeRequestUrl(apiEndpoints.procurementHqOrderTimeline, {
    month,
    centre,
    category
  })
  const response = await get(url)
  return response.data
}

// HQ acts from the tracking popup: 'remark' (set review remark) or 'resend'
// (PM only — a discrepant received order goes back out).
const hqOrderAction = async (data) => {
  if (USE_MOCK) return { ok: true }
  const response = await post(apiEndpoints.procurementHqOrderAction, data)
  return response.data
}

// Summary + per-centre detail for a month/category (multi-tab Excel / PDF).
const fetchHqBoardExport = async (month, category) => {
  if (USE_MOCK) return { title: '', month: '', summary: { columns: [], rows: [] }, centres: [] }
  const url = makeRequestUrl(apiEndpoints.procurementHqBoardExport, { month, category })
  const response = await get(url)
  return response.data
}

// Which centres are off the shared catalogue and run a roster of their own.
const fetchConsumablesCentreSettings = async () => {
  if (USE_MOCK) return { results: [] }
  const response = await get(apiEndpoints.procurementConsumablesCentreSetting)
  return response.data
}

const setConsumablesOwnRoster = async (centre, ownRosterOnly) => {
  if (USE_MOCK) return { centre, own_roster_only: ownRosterOnly }
  const response = await post(apiEndpoints.procurementConsumablesCentreSetting, {
    centre,
    own_roster_only: ownRosterOnly
  })
  return response.data
}

// Consumables catalogue: the shared items every centre sees, plus the
// centre-scoped items HQ maintains for a centre on its own roster.
const fetchConsumableItems = async (queries = {}) => {
  if (USE_MOCK) return { results: [] }
  const url = makeRequestUrl(apiEndpoints.procurementConsumableItem, {
    limit: 500,
    ...queries
  })
  const response = await get(url)
  return response.data
}

const createConsumableItem = async (data) => {
  if (USE_MOCK) return data
  const response = await post(apiEndpoints.procurementConsumableItem, data)
  return response.data
}

const updateConsumableItem = async (uuid, data) => {
  if (USE_MOCK) return data
  const response = await patch(`${apiEndpoints.procurementConsumableItem}/${uuid}/`, data)
  return response.data
}

const decideOrder = async (centre, month, decision, remarks = '') => {
  if (USE_MOCK) return mock.decideOrder(centre, month, decision, remarks)
  const response = await post(apiEndpoints.procurementOrderDecision, {
    centre,
    month,
    decision,
    remarks
  })
  return response.data
}

const addPmAttachment = async (centre, month, fileName) => {
  if (USE_MOCK) return mock.addPmAttachment(centre, month, fileName)
  const response = await post(apiEndpoints.procurementPmAttachment, {
    centre,
    month,
    file_name: fileName
  })
  return response.data
}

// ---- Attachments (P5): real file upload via direct-to-S3 presigned POST ----
// Upload one file for an entity. entityType is
// 'pm' | 'petty_cash' | 'petty_cash_entry'. `scope` is {centre, month} for the
// first two, or {entry} (a spend-line uuid) for petty_cash_entry.
// The file is sent multipart to our API, which uploads to S3 server-side — this
// avoids the browser->S3 CORS problem that broke the old presigned-POST path.
const uploadAttachment = async (entityType, scope, file) => {
  if (USE_MOCK) return mock.uploadAttachment(entityType, scope, file)
  const form = new FormData()
  form.append('entity_type', entityType)
  Object.entries(scope).forEach(([k, v]) => form.append(k, v))
  form.append('file', file)
  // Raw fetch (not the axios instance, which forces application/json) so the
  // browser sets the multipart boundary; replicate the app's auth headers.
  const token = getAccessToken()
  const response = await fetch(apiEndpoints.procurementAttachmentUpload, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      source: 'alfred'
    },
    body: form
  })
  if (!response.ok) {
    let message = 'Upload failed. Please try again.'
    try {
      const err = await response.json()
      message = err.detail || err.message || message
    } catch (e) {
      /* non-JSON error body */
    }
    throw new Error(message)
  }
  return response.json()
}

// Upload a photo of a wanted PM item and get back its S3 key + a view URL. The
// key is held on the order line (reference_image) and saved with the line — not
// as an attachment row — so it survives the draft save rewriting the lines.
const uploadItemImage = async (file) => {
  if (USE_MOCK) return mock.uploadItemImage(file)
  const form = new FormData()
  form.append('file', file)
  const token = getAccessToken()
  const response = await fetch(apiEndpoints.procurementPmItemImage, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      source: 'alfred'
    },
    body: form
  })
  if (!response.ok) {
    let message = 'Upload failed. Please try again.'
    try {
      const err = await response.json()
      message = err.detail || err.message || message
    } catch (e) {
      /* non-JSON error body */
    }
    throw new Error(message)
  }
  return response.json()
}

const fetchAttachments = async (entityType, scope) => {
  if (USE_MOCK) return mock.fetchAttachments(entityType, scope)
  const url = makeRequestUrl(apiEndpoints.procurementAttachments, {
    entity_type: entityType,
    ...scope
  })
  const response = await get(url)
  return response.data
}

// Per-line receipt confirmation: lines = [{uuid, receipt_status, receipt_note}].
const confirmReceipt = async (centre, month, lines = []) => {
  if (USE_MOCK) return mock.confirmReceipt(centre, month, lines)
  const response = await post(apiEndpoints.procurementProgramMaterialReceipt, {
    centre,
    month,
    lines
  })
  return response.data
}

const setOrderLineStatuses = async (centre, month, lines, markSent = false) => {
  if (USE_MOCK) return mock.setOrderLineStatuses(centre, month, lines, markSent)
  const response = await post(apiEndpoints.procurementOrderLineStatus, {
    centre,
    month,
    lines,
    mark_sent: markSent
  })
  return response.data
}

// HQ partial approval: accept/reject lines. Pass `all: 'accepted'|'rejected'`
// to bulk-set every line, or `lines: [{uuid, line_decision}]` for per-line.
const setOrderLineDecisions = async (centre, month, { lines, all } = {}) => {
  if (USE_MOCK) return mock.setOrderLineDecisions(centre, month, { lines, all })
  const response = await post(apiEndpoints.procurementOrderLineDecision, {
    centre,
    month,
    lines,
    all
  })
  return response.data
}

// Centre leaves a remark on its order (from the notification screen) after HQ acts.
const saveOrderFeedback = async (centre, month, feedback) => {
  if (USE_MOCK) return mock.saveOrderFeedback(centre, month, feedback)
  const response = await post(apiEndpoints.procurementOrderFeedback, {
    centre,
    month,
    feedback
  })
  return response.data
}

// HQ housekeeping: delete an order, or reopen it for the centre to edit.
const orderAdminAction = async (centre, month, action) => {
  if (USE_MOCK) return mock.orderAdminAction(centre, month, action)
  const response = await post(apiEndpoints.procurementOrderAdminAction, {
    centre,
    month,
    action
  })
  return response.data
}

// Sync the typed-but-unsubmitted rows to the server (the draft sidecar) so
// HQ can see planned spend. Empty lines clear the server-side draft.
const savePettyCashDraft = async ({ centre, month, flow, lines }) => {
  if (USE_MOCK) return mock.savePettyCashDraft({ centre, month, flow, lines })
  const response = await put(apiEndpoints.procurementPettyCashDraft, {
    centre,
    month,
    flow,
    lines
  })
  return response.data
}

// ---- Petty cash: HQ allocation / centre acknowledge / HQ freeze ----
const fetchPettyCashAllocations = async (queries = {}) => {
  if (USE_MOCK) return mock.fetchPettyCashAllocations(queries)
  const url = makeRequestUrl(apiEndpoints.procurementPettyCashAllocation, queries)
  const response = await get(url)
  return response.data
}

const fetchPettyCashOverview = async (month) => {
  if (USE_MOCK) return mock.fetchPettyCashOverview(month)
  const url = makeRequestUrl(apiEndpoints.procurementPettyCashOverview, { month })
  const response = await get(url)
  return response.data
}

const savePettyCashAllocation = async (data) => {
  if (USE_MOCK) return mock.savePettyCashAllocation(data)
  const response = await post(apiEndpoints.procurementPettyCashAllocation, data)
  return response.data
}

const acknowledgePettyCash = async (centre, month) => {
  if (USE_MOCK) return mock.acknowledgePettyCash(centre, month)
  const response = await post(apiEndpoints.procurementPettyCashAcknowledge, {
    centre,
    month
  })
  return response.data
}

const setPettyCashFreeze = async (centre, month, frozen = true) => {
  if (USE_MOCK) return mock.setPettyCashFreeze(centre, month, frozen)
  const response = await post(apiEndpoints.procurementPettyCashFreeze, {
    centre,
    month,
    frozen
  })
  return response.data
}

// HQ override: keep a centre's petty cash editable past the 3rd-of-next-month
// auto-freeze.
const unlockPettyCash = async (centre, month, unlocked = true) => {
  if (USE_MOCK) return mock.unlockPettyCash(centre, month, unlocked)
  const response = await post(apiEndpoints.procurementPettyCashUnlock, {
    centre,
    month,
    unlocked
  })
  return response.data
}

const fetchNotifications = async (centre) => {
  if (USE_MOCK) return mock.fetchNotifications()
  const url = centre
    ? makeRequestUrl(apiEndpoints.procurementNotifications, { centre })
    : apiEndpoints.procurementNotifications
  const response = await get(url)
  return response.data
}

const markNotificationsRead = async (centre) => {
  if (USE_MOCK) return mock.markNotificationsRead()
  const response = await post(apiEndpoints.procurementNotifications, {
    action: 'mark_read',
    ...(centre ? { centre } : {})
  })
  return response.data
}

// ---- SOP (per-category "how to use" text; HQ-editable) ----
const fetchSops = async () => {
  if (USE_MOCK) return mock.fetchSops()
  const response = await get(apiEndpoints.procurementSop)
  return response.data
}

const updateSop = async (data) => {
  if (USE_MOCK) return mock.updateSop(data)
  const response = await patch(apiEndpoints.procurementSop, data)
  return response.data
}

// The monthly order window (Gordon's start/deadline days) the centre board's
// banner runs on. Pass centre to also learn its PM submission state.
const fetchCycleWindow = async (centre) => {
  const response = await get(
    makeRequestUrl(
      apiEndpoints.procurementCycleWindow,
      centre ? { centre } : {}
    )
  )
  return response.data
}

// ---- Afterschool partial orders (order the gap IN PARTS) ----
const fetchAfterschoolOrder = async (centre, month) => {
  const response = await get(
    makeRequestUrl(apiEndpoints.afterschoolOrder, { centre, month })
  )
  return response.data
}

// status 'draft' saves freely; 'submitted' stamps a PO and locks the month.
const saveAfterschoolOrder = async (data) => {
  const response = await post(apiEndpoints.afterschoolSave, data)
  return response.data
}

const fetchAfterschoolPending = async () => {
  const response = await get(apiEndpoints.afterschoolPending)
  return response.data
}

const afterschoolDecision = async (data) => {
  const response = await post(apiEndpoints.afterschoolDecision, data)
  return response.data
}

// The games/items master (backend copy of the old FE seed); HQ saves
// pricing edits against it.
const fetchAfterschoolCatalogue = async () => {
  const response = await get(apiEndpoints.afterschoolCatalogue)
  return response.data
}

const saveAfterschoolCatalogue = async (data) => {
  const response = await post(apiEndpoints.afterschoolCatalogueSave, data)
  return response.data
}

// ---- Monthly CAPEX cycle (September 2026 onward) ----
// month is 'YYYY-MM'; the backend normalizes to the 1st.
const fetchCapexListItems = async (month) => {
  const response = await get(makeRequestUrl(apiEndpoints.capexListItems, { month }))
  return response.data
}

// HQ publishes the month's curated item set (replaces it wholesale).
const saveCapexListItems = async (month, items) => {
  const response = await post(apiEndpoints.capexListItems, { month, items })
  return response.data
}

const fetchCapexBudgets = async (month, centre) => {
  const response = await get(
    makeRequestUrl(apiEndpoints.capexBudgets, {
      month,
      ...(centre ? { centre } : {})
    })
  )
  return response.data
}

const saveCapexBudgets = async (month, budgets) => {
  const response = await post(apiEndpoints.capexBudgets, { month, budgets })
  return response.data
}

// The centre form's whole payload: items + saved qty + budget + month status.
const fetchCapexSheet = async (centre, month) => {
  const response = await get(
    makeRequestUrl(apiEndpoints.capexSheet, { centre, month })
  )
  return response.data
}

// status 'draft' saves freely; 'submitted' enforces the hard budget block.
const saveCapexSheet = async (data) => {
  const response = await post(apiEndpoints.capexSave, data)
  return response.data
}

const fetchCapexPending = async () => {
  const response = await get(apiEndpoints.capexPending)
  return response.data
}

// All-centres board for one capex month (mirrors the Monthly Cadence board).
const fetchCapexBoard = async (month) => {
  const response = await get(makeRequestUrl(apiEndpoints.capexBoard, { month }))
  return response.data
}

const capexDecision = async (data) => {
  const response = await post(apiEndpoints.capexDecision, data)
  return response.data
}

// Centre confirms receipt of an ordered ('sent') capex month, line by line.
// Partial is fine — capex arrives in trickles; the month flips to 'received'
// once no line is pending. lines: [{ id, receipt_status, receipt_note }].
const confirmCapexReceipt = async (centre, month, lines = []) => {
  const response = await post(apiEndpoints.capexReceipt, {
    centre,
    month,
    lines
  })
  return response.data
}

export {
  fetchBudgets,
  createBudget,
  updateBudget,
  deleteBudget,
  transferBudget,
  fetchCentres,
  fetchMyCentres,
  fetchPettyCash,
  fetchRmRegister,
  fetchRmReco,
  createRmRegisterLine,
  updateRmRegisterLine,
  deleteRmRegisterLine,
  createPettyCash,
  createPettyCashBatch,
  deletePettyCash,
  updatePettyCash,
  fetchPettyCashSummary,
  fetchConsumableRoster,
  saveConsumableRoster,
  fetchConsumablesHistory,
  reopenConsumables,
  fetchConsumablesPending,
  decideConsumables,
  confirmConsumablesReceipt,
  fetchConsumablesCentreSettings,
  setConsumablesOwnRoster,
  fetchProgramMaterial,
  saveProgramMaterial,
  searchCatalogue,
  fetchCatalogueBrowse,
  saveCatalogueItem,
  fetchProgramMaterialHistory,
  fetchCentreDashboard,
  fetchAllOrders,
  fetchHqMonthlyBoard,
  fetchHqMonthlyBoardDetail,
  fetchHqOrderTimeline,
  hqOrderAction,
  fetchHqBoardExport,
  fetchConsumableItems,
  createConsumableItem,
  updateConsumableItem,
  decideOrder,
  addPmAttachment,
  uploadAttachment,
  uploadItemImage,
  fetchAttachments,
  confirmReceipt,
  setOrderLineStatuses,
  setOrderLineDecisions,
  saveOrderFeedback,
  orderAdminAction,
  fetchPettyCashAllocations,
  fetchPettyCashOverview,
  savePettyCashAllocation,
  savePettyCashDraft,
  acknowledgePettyCash,
  setPettyCashFreeze,
  unlockPettyCash,
  fetchNotifications,
  markNotificationsRead,
  fetchSops,
  updateSop,
  fetchCycleWindow,
  fetchAfterschoolOrder,
  saveAfterschoolOrder,
  fetchAfterschoolPending,
  afterschoolDecision,
  fetchAfterschoolCatalogue,
  saveAfterschoolCatalogue,
  fetchCapexListItems,
  saveCapexListItems,
  fetchCapexBudgets,
  saveCapexBudgets,
  fetchCapexSheet,
  saveCapexSheet,
  fetchCapexPending,
  fetchCapexBoard,
  capexDecision,
  confirmCapexReceipt
}
